// DataEncryption.tsx
import React from "react";
import Hyperlink from "../../../../components/elements/HyperLink";

interface EncryptionSection {
  title: string;
  items: {
    title: string;
    description: string;
  }[];
}

const DataEncryption: React.FC = () => {
  const encryptionSections: EncryptionSection[] = [
    {
      title: "Data Encryption in Transit",
      items: [
        {
          title: "How it works",
          description:
            'When you access our website or API, an encrypted connection is established using strong ciphers. This prevents eavesdropping, tampering, or "man-in-the-middle" attacks.',
        },
        {
          title: "Protection",
          description:
            "This secures all login credentials, candidate personal data, video uploads, and any other information sent over the public internet.",
        },
        {
          title: "Configuration",
          description:
            "We enforce HTTPS Strict Transport Security (HSTS) to ensure that your browser will only ever connect to our service over a secure channel.",
        },
      ],
    },
    {
      title: "Data Encryption at Rest",
      items: [
        {
          title: "How it works",
          description:
            "Data is encrypted before it is written to the disk. This includes database encryption for account information, job descriptions, candidate metadata, and storage encryption for files like resumes, cover letters, and interview videos.",
        },
        {
          title: "Protection",
          description:
            "This measure protects your data from unauthorized access at the physical storage level.",
        },
        {
          title: "Key Management",
          description:
            "All encryption keys are managed using secure, audited key management systems (KMS) provided by our cloud partners (e.g., AWS KMS, Azure Key Vault), with strict access controls and key rotation policies.",
        },
      ],
    },
    {
      title: "Encryption of Backups",
      items: [
        {
          title: "Security Assurance",
          description:
            "All data backups are also encrypted using AES-256 before being stored, ensuring that your archived data is just as secure as your live production data.",
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white py-5 px-3 sm:px-3 lg:px-8">
      <div className="max-w-[1300px] mx-auto px-3">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-[20px] md:text-[35px] text-gray-900 mb-4">
            Data Encryption
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            At{" "}
            <span className="font-semibold text-gray-900">25th & Staffing</span>
            , protecting the confidentiality and integrity of your data is a top
            priority. We employ industry-standard encryption protocols to secure
            your data at every stage.
          </p>
        </div>

        {/* Encryption Sections */}
        <div className="space-y-5">
          {encryptionSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="space-y-4">
              <div className="text-center">
                <h2 className="text-3xl font-semibold text-gray-900">
                  {section.title}
                </h2>
              </div>

              <div className="grid gap-4 md:grid-cols-1">
                {section.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="space-y-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-6 text-center space-y-4">
          <div className="space-y-4">
            <h3 className="text-2xl font-semibold text-gray-900">
              Need More Information?
            </h3>
            <p className="text-gray-600 text-lg">
              For further questions regarding our encryption practices, please
              contact our security team.
            </p>
          </div>

          <div>
            <Hyperlink
              href="mailto:privacy@25thmail.com"
              className="text-primary"
            >
              privacy@25thmail.com
            </Hyperlink>
          </div>
        </div>

        {/* Security Standards */}
        <div className="mt-6 pt-6 border-t border-[gray]/20">
          <div className="text-center space-y-4">
            <h4 className="text-lg font-semibold text-gray-900">
              Security Standards We Implement
            </h4>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="px-4 py-2 bg-gray-100 text-gray-700">
                TLS 1.2+
              </div>
              <div className="px-4 py-2 bg-gray-100 text-gray-700">
                AES-256 Encryption
              </div>
              <div className="px-4 py-2 bg-gray-100 text-gray-700">
                HSTS Enforcement
              </div>
              <div className="px-4 py-2 bg-gray-100 text-gray-700">
                Secure Key Management
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataEncryption;
